// app.projects — the PLATFORM's project lifecycle, as a typed module.
//
// Creating and deleting projects is root-app business: the handlers
// (`provision-project` / `delete-project`) run in the platform runtime and do
// their privileged work with the decrypted `secrets/aws-provisioning` key —
// nothing here holds any AWS admin rights. This module exists so the function
// names, payloads and result shapes live in ONE place instead of being
// re-encoded by every UI that calls `app.fn("provision-project")` raw.
//
// Non-null only on the ROOT project's client: the handlers live in the
// platform runtime, and that's whose Lambdas this client invokes. (Server-side
// the handlers still verify the caller — self-service create, admin-gated
// delete — this module is convenience, not authority.)

import type { CallOpts, CallsModule } from "./calls.ts";
import { ENV_NAME_RE } from "./conventions.ts";

export interface CreateProjectInput {
  /** IGNORED when `environmentOf` is set — an environment's slug is derived
   *  (`{base}-{env}`), never requested. */
  slug: string;
  name: string;
  /** Recorded on the manifest; the CALLER (verified server-side) is seeded as
   *  the project's operator-owner regardless. */
  ownerEmail?: string;
  /** Create an ENVIRONMENT of this base project instead of a standalone one.
   *  Server-side this requires the verified caller to hold
   *  `{environmentOf}:operator-owner` — you can only speak for a project you
   *  already own. */
  environmentOf?: string;
  /** The environment's name (`"dev"`, `"staging"`). Required with
   *  `environmentOf`. */
  environment?: string;
}

/** Result of `provision-project` (see the platform handler). */
export interface CreateProjectResult {
  slug: string;
  manifestKey?: string;
  bucket?: string;
  poolId?: string;
  domain?: string;
  clientId?: string;
  outcome: "provisioned" | "already-existed";
  /** Whether the creator was seeded as `{slug}:operator-owner` in the
   *  platform pool (console access + `s3p deploy` via their platform
   *  identity — no per-project credential). */
  operatorOwnerSeeded?: boolean;
}

/** Result of `delete-project` (see the platform handler). */
export interface DeleteProjectResult {
  slug: string;
  outcome: "deleted";
  deleted?: {
    lambdaFunctions: number;
    buckets: number;
    userPools: number;
    identityPools: number;
    roles: number;
    oidcProviders: number;
    kmsKeys: number;
    manifestRemoved: boolean;
  };
}

/** Slug rule shared by create forms and the server function. */
export const PROJECT_SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

/** Result of `link-environment`. */
export interface LinkEnvironmentResult {
  slug: string;
  base?: string;
  environment?: string;
  outcome: "linked" | "unlinked";
  /** unlink only: the base it was detached from. */
  was?: string;
}

export interface ProjectsModule {
  /** Provision a new project (bucket, pools, roles, runtime — the works).
   *  Long-running: default timeout 4 minutes. The in-flight record is visible
   *  via `app.functions.listInflight({ function: "provision-project" })`, so a
   *  reload can re-attach with `awaitResult`. */
  create(input: CreateProjectInput, opts?: CallOpts): Promise<CreateProjectResult>;
  /** Tear a project down — the inverse sweep. Admin-gated server-side. */
  delete(slug: string, opts?: CallOpts): Promise<DeleteProjectResult>;
  /** Adopt the EXISTING project `{base}-{environment}` as an environment of
   *  `base`. The retrofit path for projects created before environments
   *  existed — it writes the same two records provisioning would have.
   *
   *  Requires operator-ownership of BOTH projects (verified server-side):
   *  owning only the base would annex someone else's project, owning only the
   *  child would let it declare itself someone else's environment. The slug is
   *  derived, so a project can only be adopted under the name `--env` would
   *  look for. */
  linkEnvironment(
    base: string,
    environment: string,
    opts?: CallOpts,
  ): Promise<LinkEnvironmentResult>;
  /** Detach an environment, making it a standalone project again. Needs
   *  ownership of the environment itself. */
  unlinkEnvironment(slug: string, opts?: CallOpts): Promise<LinkEnvironmentResult>;
}

export function createProjects(cfg: { calls: CallsModule }): ProjectsModule {
  return {
    async create(input, opts = {}) {
      if (input.environmentOf) {
        // The slug is the server's to derive; validating the caller's here
        // would imply we honour it.
        if (!PROJECT_SLUG_RE.test(input.environmentOf)) {
          throw new Error(
            `app.projects.create: invalid environmentOf '${input.environmentOf}'`,
          );
        }
        if (!input.environment || !ENV_NAME_RE.test(input.environment)) {
          throw new Error(
            `app.projects.create: environmentOf needs an environment name matching [a-z0-9][a-z0-9-]*, got '${input.environment ?? ""}'`,
          );
        }
      } else if (!PROJECT_SLUG_RE.test(input.slug)) {
        throw new Error(
          `app.projects.create: invalid slug '${input.slug}' (lowercase letters, digits, dashes)`,
        );
      }
      return (await cfg.calls.invoke("provision-project", input, {
        timeoutMs: 240_000,
        pollIntervalMs: 2_000,
        ...opts,
      })) as CreateProjectResult;
    },
    async delete(slug, opts = {}) {
      return (await cfg.calls.invoke("delete-project", { slug }, {
        timeoutMs: 180_000,
        pollIntervalMs: 2_000,
        ...opts,
      })) as DeleteProjectResult;
    },
    async linkEnvironment(base, environment, opts = {}) {
      if (!PROJECT_SLUG_RE.test(base)) {
        throw new Error(`app.projects.linkEnvironment: invalid base '${base}'`);
      }
      if (!ENV_NAME_RE.test(environment)) {
        throw new Error(
          `app.projects.linkEnvironment: invalid environment name '${environment}'`,
        );
      }
      return (await cfg.calls.invoke(
        "link-environment",
        { action: "link", base, environment },
        { timeoutMs: 60_000, ...opts },
      )) as LinkEnvironmentResult;
    },
    async unlinkEnvironment(slug, opts = {}) {
      if (!PROJECT_SLUG_RE.test(slug)) {
        throw new Error(`app.projects.unlinkEnvironment: invalid slug '${slug}'`);
      }
      return (await cfg.calls.invoke(
        "link-environment",
        { action: "unlink", slug },
        { timeoutMs: 60_000, ...opts },
      )) as LinkEnvironmentResult;
    },
  };
}
